/**
 * ImportDealsWizard - Orquestrador do wizard de importação em massa de negócios.
 *
 * Wizard de 6 etapas (cada etapa em seu próprio arquivo em ./steps/):
 *   1. Upload CSV               (Step1Upload)
 *   2. Mapeamento de colunas    (Step2Mapping)
 *   3. Defaults & Opções        (Step3Defaults)
 *   4. Validando (transição)    (Step4Validating)
 *   5. Aprovação explícita      (Step5Approval)
 *   6. Progresso & Resultado    (Step6Progress)
 *
 * CHANGELOG:
 *   RELEASE: 2026-05-17 v3.2.0 - Botão "Cancelar importação" disponível
 *            diretamente no Step 6 (Progresso) via prop onCancel passada ao
 *            Step6Progress. AlertDialog de confirmação aqui no wizard reusa
 *            importApi.cancelJob (RPC cancel_deal_import_job já existente).
 *            O usuário agora não precisa fechar o modal e ir na listagem
 *            para abortar uma importação em andamento.
 *   RELEASE: 2026-05-16 v3.1.4 - Validação resiliente para importações grandes
 *            (10k, 20k+): em caso de timeout (57014) ou falha pontual NÃO
 *            cancela o job — apenas retorna para Etapa 3 com toast explicando
 *            que o usuário pode clicar "Validar" novamente. A RPC v1.0.5
 *            valida em chunks server-side; o hook itera até concluir.
 *            wizard foi aberto em modo retomado (isResumed) — nesse caso o
 *            frontend não tem mais o CSV/rows/mapping em memória, então cair
 *            na Etapa 3 produzia estado impossível (Linhas=0) e o Validar
 *            seguinte falhava silenciosamente. Também adiciona guard em
 *            handleValidate para abortar com toast claro quando rows/mapping
 *            estão vazios, e proteção em handleBackFromApproval contra uso
 *            indevido em modo retomado.
 *   RELEASE: 2026-05-16 v3.1.2 - Voltar da Etapa 5 (Aprovação) agora cancela
 *            o job validado órfão no servidor e zera jobId/counters antes de
 *            retornar para a Etapa 3. Sem isso, o próximo clique em "Validar"
 *            falhava silenciosamente (createJob rejeitava por job pendente do
 *            mesmo tenant) e o usuário tinha a impressão de "nada aconteceu".
 *            Toast de erro de validação agora dura 12s e tem título
 *            específico quando há job anterior bloqueando.
 *   RELEASE: 2026-05-16 v3.1.1 - Corrigido travamento ao clicar em "Voltar"
 *            na Etapa 5 (Aprovação): agora pula a Etapa 4 (Validating, que
 *            é apenas transição interna do handleValidate) e retorna direto
 *            para a Etapa 3 (Configuração), preservando todo o estado.
 *   RELEASE: 2026-05-16 v3.1.0 - Corrigido scroll cronicamente quebrado:
 *            wrapper de conteúdo dos steps 1-4 agora é overflow-y-auto
 *            (resolve campos cortados na Etapa 2 Mapeamento). Steps 5-6
 *            mantêm flex column com scroll interno próprio.
 *   RELEASE: 2026-05-16 v3.0.0 - Refatoração visual: substituído Sheet lateral
 *            por layout "Premium Wizard" (Dialog max-w-5xl + sidebar azul 280px
 *            + resumo dinâmico), padronizando com ImportListWizard e demais
 *            wizards do sistema. Nenhuma mudança em lógica/handlers.
 *   RELEASE: 2026-05-16 v2.0.0 - Refatorado: cada step extraído para
 *            ./steps/Step{N}*.tsx.
 *   RELEASE: 2026-05-15 v1.0.0 - Versão inicial monolítica.
 */

import { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import { parseCSV, type CSVRow } from '@/lib/csv-utils';
import { useDealImport } from '@/hooks/useDealImport';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import {
  Loader2,
  X,
  ChevronLeft,
  Check,
  Upload,
  Columns3,
  Settings2,
  CheckCircle2,
  ShieldCheck,
  Activity,
  Briefcase,
  ListChecks,
  CheckCheck,
  AlertTriangle,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { createLogger } from '@/lib/logger';

import { Step1Upload } from './steps/Step1Upload';
import { Step2Mapping } from './steps/Step2Mapping';
import { Step3Defaults } from './steps/Step3Defaults';
import { Step4Validating } from './steps/Step4Validating';
import { Step5Approval } from './steps/Step5Approval';
import { Step6Progress } from './steps/Step6Progress';
import { AUTODETECT, DEAL_FIELDS, type Pipeline, type Phase, type Workspace, type ValidationCounters, type SellerMode } from './steps/types';
import { LEAD_SOURCES } from '@/lib/lead-sources';

interface Props {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  onComplete?: () => void;
  /**
   * Quando definido, abre o wizard num job existente, pulando upload/mapeamento.
   * Usado pelas ações "Continuar aprovação" (step 5) e "Ver progresso" (step 6)
   * da listagem em CrmDealsImport.
   */
  resume?: { jobId: string; step: 5 | 6 } | null;
}

// Definição centralizada dos 6 steps (label, ícone, descrição).
const STEPS = [
  { key: 'upload',     label: 'Upload',       icon: Upload,        description: 'Selecione o arquivo CSV de negócios' },
  { key: 'mapping',    label: 'Mapeamento',   icon: Columns3,      description: 'Mapeie as colunas do CSV' },
  { key: 'defaults',   label: 'Configuração', icon: Settings2,     description: 'Pipeline, fase e opções padrão' },
  { key: 'validating', label: 'Validação',    icon: CheckCircle2,  description: 'Verificação server-side' },
  { key: 'approval',   label: 'Aprovação',    icon: ShieldCheck,   description: 'Revise e aprove a importação' },
  { key: 'progress',   label: 'Progresso',    icon: Activity,      description: 'Acompanhe o processamento' },
] as const;

const logger = createLogger('ImportDealsWizard');

export function ImportDealsWizard({ open, onOpenChange, onComplete, resume }: Props) {
  const { toast } = useToast();
  const { currentTenant } = useAuth();
  const tenantId = currentTenant?.id;
  const importApi = useDealImport();

  // ===== Estado do wizard =====
  const [step, setStep] = useState(1); // 1..6
  const [jobName, setJobName] = useState('');
  const [headers, setHeaders] = useState<string[]>([]);
  const [rows, setRows] = useState<CSVRow[]>([]);
  const [mapping, setMapping] = useState<Record<string, string>>({});
  const [defaultWorkspace, setDefaultWorkspace] = useState<string>('');
  const [defaultPipeline, setDefaultPipeline] = useState<string>('');
  const [defaultPhase, setDefaultPhase] = useState<string>('');
  const [defaultSource, setDefaultSource] = useState<string>(LEAD_SOURCES[LEAD_SOURCES.length - 1]?.id ?? '');
  const [silentMode, setSilentMode] = useState(true);
  const [duplicateBehavior, setDuplicateBehavior] = useState<'skip' | 'create_new'>('skip');
  const [workspaces, setWorkspaces] = useState<Workspace[]>([]);
  const [pipelines, setPipelines] = useState<Pipeline[]>([]);
  const [phases, setPhases] = useState<Phase[]>([]);
  const [jobId, setJobId] = useState<string | null>(null);
  const [validationCounters, setValidationCounters] = useState<ValidationCounters | null>(null);
  // v3.1.5 - progresso parcial do dry-run em chunks (alimenta Step4Validating)
  const [validationProgress, setValidationProgress] = useState<ValidationCounters | null>(null);
  // v1.2.0 - vendedor (Manual | Fila | Vindo do arquivo) + tags auto
  const [sellerMode, setSellerMode] = useState<SellerMode>('manual');
  const [defaultSellerId, setDefaultSellerId] = useState<string | null>(null);
  const [defaultQueueId, setDefaultQueueId] = useState<string | null>(null);
  const [autoCreateTags, setAutoCreateTags] = useState(true);
  // Flag para diferenciar job criado nesta sessão (auto-cancela ao fechar) de
  // job retomado via prop `resume` (NÃO cancela ao fechar).
  const [isResumed, setIsResumed] = useState(false);
  // v3.2.0 - confirmação de cancelamento dentro do Step 6
  const [confirmCancelInWizard, setConfirmCancelInWizard] = useState(false);

  const currentStepIndex = step - 1;
  const currentStepData = STEPS[currentStepIndex];
  const StepIcon = currentStepData.icon;

  // Reset completo ao fechar.
  useEffect(() => {
    if (!open) {
      setStep(1);
      setJobName('');
      setHeaders([]);
      setRows([]);
      setMapping({});
      setJobId(null);
      setValidationCounters(null);
      setDefaultWorkspace('');
      setDefaultPipeline('');
      setDefaultPhase('');
      setIsResumed(false);
      // v1.2.0
      setSellerMode('manual');
      setDefaultSellerId(null);
      setDefaultQueueId(null);
      setAutoCreateTags(true);
    }
  }, [open]);

  // Quando abrir em modo "resume", busca o job (via RPC server-side) e hidrata
  // os counters/jobId/step. Substitui SELECT direto em deal_import_jobs.
  useEffect(() => {
    if (!open || !resume || !tenantId) return;
    let cancelled = false;
    (async () => {
      try {
        const { data, error } = await supabase.rpc('get_deal_import_job_summary', {
          p_job_id: resume.jobId,
          p_tenant_hint: tenantId,
        } as any);
        if (error) throw error;
        const row = Array.isArray(data) ? data[0] : data;
        if (cancelled || !row) return;
        setJobId(row.id);
        setJobName(row.name ?? '');
        setValidationCounters({
          total_rows: row.total_rows ?? 0,
          valid_count: row.valid_count ?? 0,
          invalid_count: row.invalid_count ?? 0,
          duplicate_count: row.duplicate_count ?? 0,
          blacklisted_count: row.blacklisted_count ?? 0,
        });
        setIsResumed(true);
        setStep(resume.step);
      } catch (err: any) {
        logger.error('Falha ao retomar job de importação', { jobId: resume.jobId, error: err?.message });
        toast({
          title: 'Não foi possível abrir o job',
          description: err?.message ?? 'Erro desconhecido — feche e tente novamente.',
          variant: 'destructive',
          duration: 8000,
        });
        // Não fechar automaticamente: deixa o usuário ver o motivo e decidir.
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, resume?.jobId, resume?.step, tenantId]);

  // Carrega workspaces do tenant ao abrir (via RPC get_crm_workspaces_with_stats).
  useEffect(() => {
    if (!open || !tenantId) return;
    (async () => {
      try {
        const { data, error } = await supabase.rpc('get_crm_workspaces_with_stats', {
          p_tenant_hint: tenantId,
        } as any);
        if (error) throw error;
        if (data) {
          setWorkspaces(
            (data as any[])
              .filter((w) => w.is_active !== false)
              .map((w) => ({ id: w.id, name: w.name } as Workspace)),
          );
        }
      } catch {
        /* silencioso */
      }
    })();
  }, [open, tenantId]);

  // Recarrega pipelines sempre que o workspace muda (via RPC server-side).
  useEffect(() => {
    if (!defaultWorkspace || !tenantId) {
      setPipelines([]);
      setDefaultPipeline('');
      setDefaultPhase('');
      return;
    }
    (async () => {
      try {
        const { data, error } = await supabase.rpc('list_crm_pipelines_for_import', {
          p_workspace_id: defaultWorkspace,
          p_tenant_hint: tenantId,
        } as any);
        if (error) throw error;
        if (data) setPipelines(data as Pipeline[]);
      } catch {
        /* silencioso */
      }
    })();
    // ao trocar de workspace, derruba pipeline/fase atuais
    setDefaultPipeline('');
    setDefaultPhase('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultWorkspace, tenantId]);

  // Recarrega fases sempre que o pipeline padrão muda (via RPC server-side).
  useEffect(() => {
    if (!defaultPipeline || !tenantId) { setPhases([]); return; }
    (async () => {
      try {
        const { data, error } = await supabase.rpc('list_crm_pipeline_phases_for_import', {
          p_pipeline_id: defaultPipeline,
          p_tenant_hint: tenantId,
        } as any);
        if (error) throw error;
        if (data) setPhases(data as Phase[]);
      } catch {
        /* silencioso */
      }
    })();
  }, [defaultPipeline, tenantId]);

  // Polling de detalhes do job ativo apenas no step 6.
  const jobDetails = importApi.useJobDetails(step === 6 ? jobId : null);

  // ============= Handlers =============

  async function handleFile(file: File) {
    if (file.size > 10 * 1024 * 1024) {
      toast({ title: 'Arquivo muito grande', description: 'Máximo 10MB', variant: 'destructive' });
      return;
    }
    const text = await file.text();
    // v3.3.0 (2026-05-18): requiredColumns=[] — validação real ocorre no Step 2
    // (mapping) + RPC validate_deal_import_job. Sem isso, o parser exigia
    // a coluna "telefone" (default do importador de contatos) e silenciava
    // CSVs com cabeçalho "contact_phone".
    const result = parseCSV(text, { requiredColumns: [] });
    if (result.errors.length) {
      logger.warn('parseCSV falhou', { errors: result.errors });
      toast({ title: 'Erro no CSV', description: result.errors[0], variant: 'destructive', duration: 10000 });
      return;
    }
    if (result.totalRows === 0) {
      toast({ title: 'CSV vazio', variant: 'destructive', duration: 10000 });
      return;
    }
    if (result.totalRows > 50000) {
      toast({ title: 'Limite excedido', description: 'Máx. 50.000 linhas', variant: 'destructive', duration: 10000 });
      return;
    }

    setHeaders(result.headers);
    setRows(result.rows);
    if (!jobName) setJobName(file.name.replace(/\.[^.]+$/, ''));

    // Autodetect de mapeamento por nome de coluna.
    const m: Record<string, string> = {};
    for (const h of result.headers) {
      const f = AUTODETECT[h.toLowerCase().trim()];
      if (f) m[f] = h;
    }
    setMapping(m);
    setStep(2);
  }

  async function handleValidate() {
    // v3.1.3 - Guard contra estado impossível: se não há CSV/linhas em memória
    // (típico de wizard aberto em modo retomado e voltado para Etapa 3), abortar
    // com mensagem clara em vez de criar job vazio e "parecer travado".
    if (rows.length === 0 || headers.length === 0) {
      toast({
        title: 'Reenvie o CSV para validar novamente',
        description:
          'Esta importação foi aberta a partir de um job já validado e o arquivo original não está mais em memória. Feche este modal e inicie uma nova importação a partir do CSV.',
        variant: 'destructive',
        duration: 12000,
      });
      return;
    }
    // v1.3.0 - valida todos os campos required do DEAL_FIELDS dinamicamente
    // (mantém paridade com o form manual: title + contact_phone obrigatórios).
    const missingRequired = DEAL_FIELDS
      .filter((f) => f.required && !mapping[f.key])
      .map((f) => f.label.replace(/\s*\(obrigatório\)\s*\*?/i, '').trim());
    if (missingRequired.length > 0) {
      toast({
        title: 'Campos obrigatórios não mapeados',
        description: `Mapeie a(s) coluna(s): ${missingRequired.join(', ')}`,
        variant: 'destructive',
      });
      setStep(2);
      return;
    }
    if (!defaultWorkspace) {
      toast({ title: 'Workspace obrigatório', description: 'Selecione o workspace para importar os negócios', variant: 'destructive' });
      return;
    }
    if (!defaultPipeline || !defaultPhase) {
      toast({ title: 'Pipeline/Fase obrigatórios', variant: 'destructive' });
      return;
    }
    // v1.2.0 - validações por modo de vendedor
    if (sellerMode === 'manual' && !defaultSellerId) {
      toast({ title: 'Vendedor padrão obrigatório', description: 'Selecione o vendedor padrão para este modo', variant: 'destructive' });
      return;
    }
    if (sellerMode === 'queue' && !defaultQueueId) {
      toast({ title: 'Fila obrigatória', description: 'Selecione a fila de distribuição', variant: 'destructive' });
      return;
    }
    if (sellerMode === 'from_file' && !mapping.seller) {
      toast({
        title: 'Coluna "Vendedor (e-mail)" não mapeada',
        description: 'Volte para o passo de mapeamento e aponte a coluna do CSV com o e-mail do vendedor.',
        variant: 'destructive',
      });
      setStep(2);
      return;
    }
    // Variável local: setJobId é assíncrono; sem isto o cleanup do catch
    // não conseguiria cancelar jobs criados na mesma execução.
    let createdJobId: string | null = null;
    try {
      setStep(4);
      setValidationProgress(null);

      // v1.2.0 - se modo != from_file, ignora coluna seller eventualmente mapeada
      const effectiveMapping = { ...mapping };
      if (sellerMode !== 'from_file' && sellerMode !== 'manual') {
        delete effectiveMapping.seller;
      }

      const newJobId = await importApi.createJob.mutateAsync({
        name: jobName || 'Importação sem nome',
        mapping: effectiveMapping,
        defaults: {
          workspace_id: defaultWorkspace,
          pipeline_id: defaultPipeline,
          phase_id: defaultPhase,
          source: defaultSource,
          // v1.2.0
          seller_mode: sellerMode,
          seller_id: sellerMode === 'manual' ? defaultSellerId : null,
          distribution_queue_id: sellerMode === 'queue' ? defaultQueueId : null,
        } as any,
        options: {
          silent_mode: silentMode,
          duplicate_behavior: duplicateBehavior,
          duplicate_window_days: 90,
          default_country_code: '55',
          // v1.2.0
          auto_create_tags: autoCreateTags,
        },
        total_rows: rows.length,
      });
      createdJobId = newJobId;
      setJobId(newJobId);

      const payload = rows.map((row, idx) => ({ row_index: idx, raw_row: row }));
      await importApi.populateItems.mutateAsync({ jobId: newJobId, rows: payload });

      // Envia lista oficial de origens para o backend rejeitar valores divergentes
      const counters = await importApi.validateJob.mutateAsync({
        jobId: newJobId,
        allowedSources: LEAD_SOURCES.map((s) => s.id),
        onProgress: (c) => setValidationProgress(c),
      });
      setValidationCounters(counters);
      setStep(5);
    } catch (err: any) {
      // Log completo para diagnóstico (mensagem, code, details, hint)
      logger.error('Falha na validação do job de importação', {
        message: err?.message,
        code: err?.code,
        details: err?.details,
        hint: err?.hint,
        jobId,
      });
      // v3.1.4 — Timeout (57014) ou erro durante a validação NÃO cancela o job.
      // A RPC v1.0.5 valida em chunks; em caso de falha pontual o job continua
      // em 'validating' e o usuário pode tentar novamente sem perder o CSV já
      // populado no servidor. Só cancelamos quando o erro impediu a CRIAÇÃO
      // ou POPULAÇÃO do job (rows ainda não estão no servidor).
      const isTimeout = err?.code === '57014';
      const populated = !!createdJobId; // já criou + populou items
      toast({
        title: isTimeout
          ? 'Validação demorou demais — vamos tentar de novo'
          : jobId
            ? 'Falha na validação — clique em Validar novamente'
            : 'Erro na validação',
        description: isTimeout
          ? 'O servidor processa em lotes. Clique em Validar novamente: a importação continua de onde parou.'
          : err?.message ?? 'Falha desconhecida ao validar',
        variant: 'destructive',
        duration: 12000,
      });
      // Só cancela job órfão se NÃO conseguimos popular os itens
      // (situação em que o job está realmente inutilizável).
      if (!populated && (createdJobId ?? jobId)) {
        const orphanId = createdJobId ?? jobId;
        if (orphanId) {
          try { await importApi.cancelJob.mutateAsync(orphanId); } catch { /* noop */ }
          setJobId(null);
        }
      }
      setStep(3);
    }
  }

  async function handleApprove() {
    // Diagnóstico: registra contexto antes de decidir.
    logger.info('approve clicked', {
      jobId,
      step,
      hasCounters: !!validationCounters,
      validCount: validationCounters?.valid_count,
    });
    // Falha explícita quando o job ainda não está hidratado (modo resume).
    if (!jobId) {
      toast({
        title: 'Job ainda não carregado',
        description: 'Aguarde alguns segundos e tente novamente.',
        variant: 'destructive',
      });
      return;
    }
    try {
      const res = await importApi.approveAndEnqueue.mutateAsync(jobId);
      toast({
        title: 'Importação iniciada',
        description: `${res.batches_created} lotes • ${res.items_enqueued} negócios na fila`,
      });
      setStep(6);
    } catch (err: any) {
      logger.error('Falha ao aprovar/enfileirar importação', {
        message: err?.message,
        code: err?.code,
        details: err?.details,
        hint: err?.hint,
        jobId,
      });
      toast({
        title: 'Erro ao confirmar importação',
        description: err?.message ?? 'Falha desconhecida ao aprovar',
        variant: 'destructive',
        duration: 8000,
      });
    }
  }

  async function handleCancel() {
    // Apenas cancela jobs criados nesta sessão. Em modo `resume`, o usuário
    // pode estar apenas espiando — fechar não deve cancelar o job real.
    if (jobId && step < 6 && !isResumed) {
      try { await importApi.cancelJob.mutateAsync(jobId); } catch { /* noop */ }
    }
    onOpenChange(false);
  }

  // v3.1.2 - Voltar da Etapa 5 (Aprovação) para a Etapa 3 (Configuração).
  // Precisa cancelar o job validado para liberar o tenant para um novo
  // createJob (caso contrário regras de unicidade/limites no RPC fazem o
  // re-Validar falhar silenciosamente e o usuário fica com a impressão de
  // "nada aconteceu"). Defaults preenchidos (workspace/pipeline/fase/seller/
  // mapping/etc.) são preservados — apenas o estado do job antigo é zerado.
  async function handleBackFromApproval() {
    // v3.1.3 - Em modo retomado (`isResumed`), o frontend NÃO tem CSV/rows/
    // mapping em memória — voltar para Etapa 3 produziria estado impossível
    // (Linhas=0) e o próximo Validar abortaria. Bloquear com toast claro.
    if (isResumed) {
      toast({
        title: 'Não é possível voltar nesta importação',
        description:
          'Este job já foi validado em outra sessão. Para alterar configurações, cancele a importação na listagem e inicie uma nova a partir do CSV.',
        variant: 'destructive',
        duration: 10000,
      });
      return;
    }
    if (jobId) {
      try { await importApi.cancelJob.mutateAsync(jobId); } catch { /* noop */ }
    }
    setJobId(null);
    setValidationCounters(null);
    setStep(3);
  }

  // Navegação pela sidebar: só permite voltar para steps já concluídos.
  function handleStepClick(targetIndex: number) {
    // Bloqueia clicar em "Validação" (step 4) que é apenas transição.
    if (targetIndex >= currentStepIndex) return;
    if (targetIndex === 3) return;
    setStep(targetIndex + 1);
  }

  const isBusy =
    importApi.createJob.isPending ||
    importApi.populateItems.isPending ||
    importApi.validateJob.isPending ||
    importApi.approveAndEnqueue.isPending;

  // ============= Render =============

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) handleCancel(); else onOpenChange(true); }}>
      <DialogContent className="max-w-5xl max-h-[90vh] h-[90vh] p-0 gap-0 flex flex-col">
        <div className="grid grid-cols-1 md:grid-cols-[280px_1fr] h-full min-h-0 flex-1">

          {/* ===== SIDEBAR PREMIUM ===== */}
          <aside
            className="hidden md:flex flex-col relative overflow-hidden"
            style={{
              background:
                'linear-gradient(135deg, hsl(210, 80%, 15%) 0%, hsl(210, 70%, 20%) 50%, hsl(200, 60%, 25%) 100%)',
            }}
          >
            {/* Blobs decorativos */}
            <div className="absolute inset-0 overflow-hidden pointer-events-none">
              <div className="absolute -top-20 -right-20 w-40 h-40 bg-white/5 rounded-full blur-3xl" />
              <div className="absolute top-1/2 -left-20 w-60 h-60 bg-white/5 rounded-full blur-3xl" />
              <div className="absolute -bottom-10 right-4 w-32 h-32 bg-white/5 rounded-full blur-2xl" />
            </div>

            <div className="relative z-10 flex flex-col h-full p-5">
              {/* Header da sidebar */}
              <div className="mb-6">
                <div className="w-12 h-12 rounded-xl bg-white/10 flex items-center justify-center mb-4">
                  <Briefcase className="h-6 w-6 text-white" />
                </div>
                <h2 className="text-lg font-semibold text-white">Importar Negócios</h2>
                <p className="text-sm text-white/60 mt-1">
                  Etapa {currentStepIndex + 1} de {STEPS.length}
                </p>
              </div>

              {/* Steps */}
              <nav className="flex-1 space-y-1">
                {STEPS.map((s, index) => {
                  const isCompleted = index < currentStepIndex;
                  const isActive = index === currentStepIndex;
                  const isPending = index > currentStepIndex;
                  const Icon = s.icon;
                  const isClickable = isCompleted && index !== 3 && !isBusy;

                  return (
                    <button
                      key={s.key}
                      type="button"
                      onClick={() => isClickable && handleStepClick(index)}
                      disabled={!isClickable && !isActive}
                      className={cn(
                        'w-full flex items-center gap-3 px-3 py-3 rounded-lg text-left transition-all duration-200',
                        isActive && 'bg-white/10 border border-white/20',
                        isClickable && 'hover:bg-white/5 cursor-pointer',
                        isPending && 'opacity-50 cursor-not-allowed',
                      )}
                    >
                      <div
                        className={cn(
                          'w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 border-2 transition-all',
                          isCompleted && 'bg-green-500 border-green-500',
                          isActive && 'bg-white border-white',
                          isPending && 'bg-transparent border-white/30',
                        )}
                      >
                        {isCompleted ? (
                          <Check className="h-4 w-4 text-white" />
                        ) : (
                          <Icon className={cn('h-4 w-4', isActive ? 'text-primary' : 'text-white/60')} />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <span className={cn('block text-sm font-medium truncate', isActive ? 'text-white' : 'text-white/80')}>
                          {s.label}
                        </span>
                        <span className="block text-xs text-white/40 truncate">{s.description}</span>
                      </div>
                    </button>
                  );
                })}
              </nav>

              {/* Resumo */}
              <div className="mt-auto pt-4 border-t border-white/10">
                <h3 className="text-xs font-medium text-white/60 uppercase tracking-wider mb-3">Resumo</h3>
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <ListChecks className="h-4 w-4 text-white/60" />
                      <span className="text-sm text-white/80">Linhas</span>
                    </div>
                    <span className="text-sm font-medium text-white">{rows.length}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <CheckCheck className="h-4 w-4 text-white/60" />
                      <span className="text-sm text-white/80">Válidos</span>
                    </div>
                    <span
                      className={cn(
                        'text-sm font-medium',
                        (validationCounters?.valid_count ?? 0) > 0 ? 'text-green-400' : 'text-white',
                      )}
                    >
                      {validationCounters?.valid_count ?? 0}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <AlertTriangle className="h-4 w-4 text-white/60" />
                      <span className="text-sm text-white/80">Problemas</span>
                    </div>
                    <span className="text-sm font-medium text-white">
                      {(validationCounters?.invalid_count ?? 0) + (validationCounters?.duplicate_count ?? 0)}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </aside>

          {/* ===== CONTEÚDO PRINCIPAL ===== */}
          <main className="flex flex-col h-full min-h-0 overflow-hidden">
            <DialogHeader className="flex-shrink-0 px-6 py-5 border-b bg-background">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                  <StepIcon className="h-6 w-6 text-primary" />
                </div>
                <div className="flex-1 text-left">
                  <DialogTitle className="text-lg font-semibold">{currentStepData.label}</DialogTitle>
                  <DialogDescription className="text-sm">{currentStepData.description}</DialogDescription>
                </div>
              </div>
            </DialogHeader>

            {/*
              Área de conteúdo do wizard.
              - Steps 1-4 são formulários simples → wrapper com scroll vertical
                automático (resolve cronicamente o problema de campos cortados,
                ex.: Etapa 2 com 11 campos de mapeamento).
              - Steps 5-6 gerenciam o próprio layout em flex column (tabs,
                tabelas com scroll interno) → wrapper sem scroll externo para
                não competir com os scrolls internos já existentes.
            */}
            {step <= 4 ? (
              <div className="flex-1 min-h-0 min-w-0 overflow-y-auto">
                <div className="p-6 pb-8">
                  {step === 1 && (
                    <Step1Upload
                      jobName={jobName}
                      onJobNameChange={setJobName}
                      onFileSelected={handleFile}
                    />
                  )}

                  {step === 2 && (
                    <Step2Mapping
                      rowCount={rows.length}
                      headers={headers}
                      mapping={mapping}
                      onMappingChange={setMapping}
                    />
                  )}

                  {step === 3 && (
                    <Step3Defaults
                  workspaces={workspaces}
                  pipelines={pipelines}
                  phases={phases}
                  defaultWorkspace={defaultWorkspace}
                  defaultPipeline={defaultPipeline}
                  defaultPhase={defaultPhase}
                  defaultSource={defaultSource}
                  silentMode={silentMode}
                  duplicateBehavior={duplicateBehavior}
                  sellerMode={sellerMode}
                  defaultSellerId={defaultSellerId}
                  defaultQueueId={defaultQueueId}
                  autoCreateTags={autoCreateTags}
                  onDefaultWorkspaceChange={setDefaultWorkspace}
                  onDefaultPipelineChange={setDefaultPipeline}
                  onDefaultPhaseChange={setDefaultPhase}
                  onDefaultSourceChange={setDefaultSource}
                  onSilentModeChange={setSilentMode}
                  onDuplicateBehaviorChange={setDuplicateBehavior}
                  onSellerModeChange={setSellerMode}
                  onDefaultSellerIdChange={setDefaultSellerId}
                  onDefaultQueueIdChange={setDefaultQueueId}
                  onAutoCreateTagsChange={setAutoCreateTags}
                />
                  )}

                  {step === 4 && (
                    <Step4Validating progress={validationProgress} totalRows={rows.length} />
                  )}
                </div>
              </div>
            ) : (
              <div
                className={
                  // Step 5 (Aprovação): permite rolar a página inteira para
                  // ver Resumo + tabela de "Linhas com problema" sem cortes.
                  // Step 6 (Progresso): mantém overflow-hidden pois o
                  // componente gerencia o próprio scroll interno.
                  step === 5
                    ? 'flex-1 min-h-0 min-w-0 overflow-y-auto p-6 flex flex-col'
                    : 'flex-1 min-h-0 min-w-0 overflow-hidden p-6 flex flex-col'
                }
              >
                {step === 5 && validationCounters && (
                  <Step5Approval counters={validationCounters} jobId={jobId} />
                )}

                {step === 6 && (
                  <Step6Progress
                    isLoading={jobDetails.isLoading}
                    details={jobDetails.data}
                    jobId={jobId}
                    error={jobDetails.error}
                    onCancel={jobId ? () => setConfirmCancelInWizard(true) : undefined}
                    cancelPending={importApi.cancelJob.isPending}
                  />
                )}
              </div>
            )}

            {/* Footer persistente */}
            <div className="flex-shrink-0 bg-background px-6 py-4 border-t flex justify-between items-center gap-2">
              <Button
                variant="outline"
                onClick={
                  step === 1 || step >= 6
                    ? handleCancel
                    // v3.1.2 - Etapa 5: usa handler dedicado que cancela job
                    // órfão, zera jobId/counters e volta para Etapa 3
                    // (preservando defaults). Demais etapas: voltar normal.
                    : step === 5
                      ? handleBackFromApproval
                      : () => setStep((s) => s - 1)
                }
                disabled={isBusy || step === 4 || (step === 5 && isResumed)}
                title={
                  step === 5 && isResumed
                    ? 'Este job foi aberto a partir da listagem e não pode ser editado sem reenviar o CSV'
                    : undefined
                }
              >
                {step === 1 || step >= 6 ? (
                  <>
                    <X className="h-4 w-4 mr-2" />
                    {step >= 6 ? 'Fechar' : 'Cancelar'}
                  </>
                ) : (
                  <>
                    <ChevronLeft className="h-4 w-4 mr-2" />
                    Voltar
                  </>
                )}
              </Button>

              {/* Dots mobile */}
              <div className="flex md:hidden items-center gap-1">
                {STEPS.map((_, index) => (
                  <div
                    key={index}
                    className={cn(
                      'h-2 rounded-full transition-all',
                      index < currentStepIndex && 'bg-green-500 w-2',
                      index === currentStepIndex && 'bg-primary w-4',
                      index > currentStepIndex && 'bg-muted w-2',
                    )}
                  />
                ))}
              </div>

              <div className="flex gap-2">
                {step === 2 && (
                  <Button
                    onClick={() => setStep(3)}
                    // v1.3.0 - desabilita até que todos os campos required estejam mapeados
                    disabled={DEAL_FIELDS.some((f) => f.required && !mapping[f.key])}
                  >
                    Próximo
                  </Button>
                )}

                {step === 3 && (
                  <Button
                    onClick={handleValidate}
                    disabled={
                      !defaultWorkspace ||
                      !defaultPipeline ||
                      !defaultPhase ||
                      (sellerMode === 'manual' && !defaultSellerId) ||
                      (sellerMode === 'queue' && !defaultQueueId) ||
                      isBusy
                    }
                  >
                    {isBusy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                    Validar
                  </Button>
                )}

                {step === 5 && validationCounters && validationCounters.valid_count > 0 && (
                  <Button
                    onClick={handleApprove}
                    disabled={!jobId || importApi.approveAndEnqueue.isPending}
                    title={!jobId ? 'Carregando job...' : undefined}
                  >
                    {importApi.approveAndEnqueue.isPending && (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    )}
                    Confirmar importação de {validationCounters.valid_count.toLocaleString('pt-BR')} negócios
                  </Button>
                )}

                {step === 6 && jobDetails.data?.job?.status === 'completed' && (
                  <Button onClick={() => { onComplete?.(); onOpenChange(false); }}>Concluir</Button>
                )}
              </div>
            </div>
          </main>
        </div>
      </DialogContent>

      {/*
        v3.2.0 - Confirmação de cancelamento dentro do wizard (Step 6).
        Reusa importApi.cancelJob (RPC cancel_deal_import_job). O batch atual
        em processamento (lock de ~2 min) termina naturalmente; os lotes
        seguintes são pulados pelo worker process_deal_import_batch.
      */}
      <AlertDialog open={confirmCancelInWizard} onOpenChange={setConfirmCancelInWizard}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Cancelar importação?</AlertDialogTitle>
            <AlertDialogDescription>
              A importação <strong>{jobName || 'em andamento'}</strong> será marcada como cancelada.
              O lote em processamento no momento termina normalmente; os lotes
              seguintes serão pulados. Esta ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={importApi.cancelJob.isPending}>
              Voltar
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={async (e) => {
                // Evita fechamento automático do AlertDialog antes do await
                e.preventDefault();
                if (!jobId) return;
                try {
                  await importApi.cancelJob.mutateAsync(jobId);
                  toast({ title: 'Importação cancelada' });
                  setConfirmCancelInWizard(false);
                } catch (err: any) {
                  toast({
                    title: 'Erro ao cancelar',
                    description: err?.message ?? 'Tente novamente.',
                    variant: 'destructive',
                  });
                }
              }}
              disabled={importApi.cancelJob.isPending || !jobId}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {importApi.cancelJob.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Cancelar importação
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Dialog>
  );
}
